import * as React from 'react'
import { ComponentProps, PropsWithChildren, useState } from 'react'
import {
    areTimelineFilterValuesEqual,
    formatLocalDateForTimeline,
    isTimelinePresetOption,
    parseTimelineDateToLocal,
} from '../../../../utils/date'
import { RadioGroupItem } from '../../Form'
import { TimelineSelect } from '../../TimelineSelect/TimelineSelect'
import { DatePicker } from '../../DatePicker'
import { FilterTypeTimelineProps, TimelineFilterValue } from '../types'
import { Filter } from '../Filter'
import { TimelineOption } from './types'
import { useFilterCallbacks } from '../useFilterCallbacks'
import { useTranslation } from 'react-i18next'
import { PAST_DATE_LIMIT, FUTURE_DATE_LIMIT } from './constants'
import { useTimelineFilterLabel } from './useTimelineFilterLabel'
import { isValidTimelineFilterValue } from './isValidTimelineFilterValue'
import { parseTimelineFilter } from './utils'

export const TimelineFilter: React.FC<FilterTypeTimelineProps> = (props) => {
    const { value: initialValue, defaultValue, onChange } = props

    const {
        setCurrentValue,
        currentValue,
        onValueSave,
        onValueReset,
        resetState,
    } = useFilterCallbacks(initialValue, defaultValue, onChange, {
        compareValues: areTimelineFilterValuesEqual,
    })

    const initialSaveDisabled = !isValidTimelineFilterValue(currentValue)
    const [saveDisabled, setSaveDisabled] = useState(initialSaveDisabled)
    const label = useTimelineFilterLabel(initialValue)

    const onTimelineChange = (value: TimelineFilterValue) => {
        setCurrentValue(value)
        setSaveDisabled(!isValidTimelineFilterValue(value))
    }

    return (
        <Filter
            {...props}
            valueLabel={label}
            onValueSave={onValueSave}
            onValueReset={onValueReset}
            resetState={resetState}
            options={props.config.options}
            scrollable={false}
            saveDisabled={saveDisabled}
            showReset={true}
            containerRef={props.containerRef}
        >
            <TimelineFilterContent
                name={props.name}
                onChange={onTimelineChange}
                value={currentValue}
            />
        </Filter>
    )
}

type TimelineFilterContentProps = {
    name: string
    value: TimelineFilterValue
    onChange: (value: TimelineFilterValue) => void
}

const TimelineFilterContent: React.FC<TimelineFilterContentProps> = (props) => {
    const { name, onChange, value: timelineFilterValue } = props

    const selectedOption = timelineFilterValue.type

    const { t } = useTranslation('translations')
    const { startDate, endDate } = parseTimelineFilter(timelineFilterValue)

    const receivedStartDate =
        startDate === formatLocalDateForTimeline(PAST_DATE_LIMIT)
            ? undefined
            : startDate
    const receivedEndDate =
        endDate === formatLocalDateForTimeline(FUTURE_DATE_LIMIT)
            ? undefined
            : endDate

    const onRadioChange = (selectedOption: TimelineOption) => {
        if (selectedOption === 'is') {
            onChange({
                type: 'is',
                value: 'last12Months',
            })
        } else {
            onChange({
                type: selectedOption,
                value: {
                    startDate: formatLocalDateForTimeline(PAST_DATE_LIMIT),
                    endDate: formatLocalDateForTimeline(FUTURE_DATE_LIMIT),
                },
            })
        }
    }

    return (
        <div
            className="space-y-1 py-2"
            onClick={(event) => {
                // react date-picker removes calendar from DOM after date is selected
                // causing useOnClickOutisde to trigger and close the filter dropdown
                // stopping propagation helps avoid that issue
                event.stopPropagation()
            }}
        >
            <TimelineFilterOption
                name={name}
                value={selectedOption}
                optionValue="is"
                optionLabel="is"
                onChange={onRadioChange}
            >
                {isTimelinePresetOption(timelineFilterValue.value) && (
                    <TimelineSelect
                        name={`${name}-timeline-select`}
                        value={timelineFilterValue.value}
                        buttonWrapperClassname="w-full"
                        buttonClassname="w-full"
                        onChange={(timelinePresetOption) => {
                            onChange({
                                type: 'is',
                                value: timelinePresetOption,
                            })
                        }}
                    />
                )}
            </TimelineFilterOption>
            <TimelineFilterOption
                name={name}
                value={selectedOption}
                optionValue="isBefore"
                optionLabel={t('filters.timeline.options.isBefore')}
                onChange={onRadioChange}
            >
                <TimelineDatePicker
                    selected={parseTimelineDateToLocal(receivedEndDate)}
                    onChange={(endDate) => {
                        if (endDate) {
                            onChange({
                                type: 'isBefore',
                                value: {
                                    startDate:
                                        formatLocalDateForTimeline(
                                            PAST_DATE_LIMIT
                                        ),
                                    endDate:
                                        formatLocalDateForTimeline(endDate),
                                },
                            })
                        }
                    }}
                    requiredErrorMessage={t(
                        'filters.timeline.errors.dateIsRequired'
                    )}
                />
            </TimelineFilterOption>
            <TimelineFilterOption
                name={name}
                value={selectedOption}
                optionValue="isAfter"
                optionLabel={t('filters.timeline.options.isAfter')}
                onChange={onRadioChange}
            >
                <TimelineDatePicker
                    selected={parseTimelineDateToLocal(receivedStartDate)}
                    onChange={(startDate) => {
                        if (startDate) {
                            onChange({
                                type: 'isAfter',
                                value: {
                                    endDate:
                                        formatLocalDateForTimeline(
                                            FUTURE_DATE_LIMIT
                                        ),
                                    startDate:
                                        formatLocalDateForTimeline(startDate),
                                },
                            })
                        }
                    }}
                    requiredErrorMessage={t(
                        'filters.timeline.errors.dateIsRequired'
                    )}
                />
            </TimelineFilterOption>
            <TimelineFilterOption
                name={name}
                value={selectedOption}
                optionValue="isBetween"
                optionLabel={t('filters.timeline.options.isBetween')}
                onChange={onRadioChange}
            >
                <TimelineDatePicker
                    startDate={parseTimelineDateToLocal(receivedStartDate)}
                    endDate={parseTimelineDateToLocal(receivedEndDate)}
                    selectsStart
                    selected={parseTimelineDateToLocal(receivedStartDate)}
                    onChange={(startDate) => {
                        if (startDate) {
                            onChange({
                                type: 'isBetween',
                                value: {
                                    startDate:
                                        formatLocalDateForTimeline(startDate),
                                    endDate:
                                        receivedEndDate ??
                                        formatLocalDateForTimeline(
                                            FUTURE_DATE_LIMIT
                                        ),
                                },
                            })
                        }
                    }}
                    requiredErrorMessage={t(
                        'filters.timeline.errors.startDateIsRequired'
                    )}
                />
                <div>{t('filters.timeline.options.and')}</div>
                <TimelineDatePicker
                    startDate={parseTimelineDateToLocal(receivedStartDate)}
                    endDate={parseTimelineDateToLocal(receivedEndDate)}
                    selectsEnd
                    selected={parseTimelineDateToLocal(receivedEndDate)}
                    onChange={(endDate) => {
                        if (endDate) {
                            onChange({
                                type: 'isBetween',
                                value: {
                                    startDate:
                                        receivedStartDate ??
                                        formatLocalDateForTimeline(
                                            PAST_DATE_LIMIT
                                        ),
                                    endDate:
                                        formatLocalDateForTimeline(endDate),
                                },
                            })
                        }
                    }}
                    requiredErrorMessage={t(
                        'filters.timeline.errors.endDateIsRequired'
                    )}
                />
            </TimelineFilterOption>
        </div>
    )
}

type TimelineFilterOptionProps = PropsWithChildren & {
    name: string
    value: string
    optionValue: TimelineOption
    optionLabel: string
    onChange: (optionValue: TimelineOption) => void
}

const TimelineFilterOption: React.FC<TimelineFilterOptionProps> = (props) => {
    const { name, optionLabel, value, optionValue, onChange, children } = props
    const checked = optionValue === value

    return (
        <>
            <RadioGroupItem
                name={name}
                value={optionValue}
                checked={checked}
                onChange={() => {
                    onChange(optionValue)
                }}
                itemWrapperClassname="py-2 px-4"
            >
                {optionLabel}
            </RadioGroupItem>
            {checked && (
                <div className="relative z-10 space-y-1 px-4 pb-2">
                    {children}
                </div>
            )}
        </>
    )
}

export const TimelineDatePicker: React.FC<ComponentProps<typeof DatePicker>> = (
    props
) => {
    return (
        <DatePicker
            wrapperClassName="w-full"
            className="w-full"
            filterDate={(date: Date) => date < new Date()}
            required={true}
            shouldCloseOnSelect={true}
            {...props}
        />
    )
}
